import React from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import config from "../config";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { LazyLoadImage } from "react-lazy-load-image-component";
import placeholderimage from "../image/placeholder.jpg";
import noimage from "../image/noimage.jpg";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

export default function Detail() {
  const [businessDetail, bussinessDetailState] = useState([]);
  const [businessDetailGallery, businessDetailGalleryState] = useState([]);
  const [planDetail, planDetailState] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    axios
      .get(`${config.apiUrl}/business-detail/${params.slug}`)
      .then((res) => {
        bussinessDetailState(res.data.business_detail);
        businessDetailGalleryState(res.data.business_detail.gallery);
        planDetailState(res.data.business_detail.plan_details);
      })
      .catch((error) => {
        navigate("/error");
      });
  }, [params.slug, navigate]);

  // Returns true if the gym is currently open based on open_time / close_time
  const isOpenNow = (openTime, closeTime) => {
    if (!openTime || !closeTime) return null;
    const clean = (t) => t.replace(/[^0-9:]/g, "").trim();
    const toMinutes = (t) => {
      const parts = clean(t).split(":");
      if (parts.length < 2) return null;
      return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    };
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    const openMins  = toMinutes(openTime);
    const closeMins = toMinutes(closeTime);
    if (openMins === null || closeMins === null) return null;
    if (openMins === 0 && closeMins === 0) return null; // default/unset
    return currentMins >= openMins && currentMins <= closeMins;
  };

  const gymIsOpen = isOpenNow(businessDetail.open_time, businessDetail.close_time);

  const state = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1921 },
      items: 1,
      slidesToSlide: 1,
    },
    desktop: {
      breakpoint: { max: 1920, min: 1025 },
      items: 1,
      slidesToSlide: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 769 },
      items: 1,
      slidesToSlide: 1,
    },
    mobile: {
      breakpoint: { max: 768, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };

  return (
    <>
      <div className="detail-container-fluid">
        <HelmetProvider>
          <Helmet>
            <title>
              {businessDetail.meta_title ||
                (businessDetail.name
                  ? `${businessDetail.name} — Gym in ${businessDetail.city} | MeraGym`
                  : "Gym Details | MeraGym")}
            </title>
            <meta
              name="keywords"
              content={
                businessDetail.meta_keyword ||
                (businessDetail.name
                  ? `${businessDetail.name}, gym in ${businessDetail.city}, fitness center ${businessDetail.city}`
                  : "gym, fitness center, meragym")
              }
            />
            <meta
              name="description"
              content={
                businessDetail.meta_description ||
                (businessDetail.name
                  ? `${businessDetail.name} is located in ${businessDetail.area || ""} ${businessDetail.address || ""}, ${businessDetail.city || ""}. Join now and start your fitness journey.`
                  : "Find gym details, membership plans and working hours on MeraGym.")
              }
            />
            {businessDetail.slug && (
              <link rel="canonical" href={`https://meragym.com/${businessDetail.slug}`} />
            )}
            <meta property="og:type" content="business.business" />
            <meta
              property="og:title"
              content={businessDetail.meta_title || (businessDetail.name ? `${businessDetail.name} | MeraGym` : "Gym Details | MeraGym")}
            />
            <meta
              property="og:description"
              content={
                businessDetail.meta_description ||
                (businessDetail.name
                  ? `${businessDetail.name} in ${businessDetail.city}. Check membership plans and working hours on MeraGym.`
                  : "Find gym details on MeraGym.")
              }
            />
            {(businessDetail.cover_image || businessDetail.thumbnail_image) && (
              <meta property="og:image" content={businessDetail.cover_image || businessDetail.thumbnail_image} />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            <meta
              name="twitter:title"
              content={businessDetail.meta_title || (businessDetail.name ? `${businessDetail.name} | MeraGym` : "Gym Details | MeraGym")}
            />
            <meta
              name="twitter:description"
              content={
                businessDetail.meta_description ||
                (businessDetail.name
                  ? `${businessDetail.name} in ${businessDetail.city}. Check membership plans on MeraGym.`
                  : "Find gym details on MeraGym.")
              }
            />
          </Helmet>
        </HelmetProvider>
        <div className="container">
          <div className="row detail-container-row g-3">
            <div className="col-12 col-lg-8">
              <div className="row detail-container-sub-row">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card">
                    <LazyLoadImage
                      src={businessDetail.cover_image || businessDetailGallery?.[0] || noimage}
                      placeholderSrc={placeholderimage}
                      onError={(e) => { e.target.onerror = null; e.target.src = noimage; }}
                      loading="lazy"
                      className="Mera-Gym-Detail"
                      alt={businessDetail.name ? `${businessDetail.name} - gym cover image` : "Gym cover image"}
                    />
                    <div className="card-body">
                      <div className="d-flex flex-row justify-content-between align-items-start">
                        <h1 className="detail-gym-name">
                          {businessDetail.name}
                        </h1>
                        {gymIsOpen !== null && (
                          <span className={`mg-open-badge ms-2 ${gymIsOpen ? "mg-open-badge--open" : "mg-open-badge--closed"}`}>
                            <i className={`bi ${gymIsOpen ? "bi-circle-fill" : "bi-circle"} me-1`}></i>
                            {gymIsOpen ? "Open Now" : "Closed"}
                          </span>
                        )}
                      </div>

                      <div className="detail_location">
                        <i className="bi bi-geo-alt"></i> &nbsp;
                        <p className="detail-gym-location">
                          {businessDetail.city}
                        </p>
                      </div>
                      <h2 className="detail-address">Address</h2>
                      <p className="detail-page-address">
                        {businessDetail.area}, {businessDetail.address},{" "}
                        {businessDetail.pincode}
                      </p>
                      <h3 className="detail-address">Available Workouts</h3>
                      <ul className="workout_list">
                        {businessDetail.category &&
                          businessDetail.category.map((catdata) => (
                            <li key={catdata.id}>
                              {catdata.image && (
                                <img
                                  src={catdata.image}
                                  alt={catdata.name}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              )}
                              {catdata.name}
                            </li>
                          ))}
                      </ul>
                      <h3 className="detail-address">Amenities</h3>
                      <ul className="workout_list">
                        {businessDetail.amenity &&
                          businessDetail.amenity.map((amenity, index) => (
                            <li key={index}>
                              {amenity.image && (
                                <img
                                  src={amenity.image}
                                  alt={amenity.name}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              )}
                              {amenity.name}
                            </li>
                          ))}
                      </ul>
                      {businessDetail.open_time && businessDetail.close_time &&
                       businessDetail.open_time !== "00:00" && businessDetail.close_time !== "00:00" &&
                       businessDetail.open_time !== "00.00" && businessDetail.close_time !== "00.00" && (
                        <>
                          <h3 className="detail-address">Gym Working Hours</h3>
                          <ul className="policies">
                            <li>
                              <p>Opens</p>
                              <h6>{businessDetail.open_time}</h6>
                            </li>
                            <li>
                              <p>Closes</p>
                              <h6>{businessDetail.close_time}</h6>
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                    <div className="social_down">
                      <div className="dropdown">
                        <button
                          className="btn"
                          type="button"
                          id="sociallink"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <i className="bi bi-three-dots-vertical"></i>
                        </button>
                        <ul
                          className="dropdown-menu"
                          aria-labelledby="sociallink"
                        >
                          {businessDetail.facebook_link && (
                            <li>
                              <a
                                className="dropdown-item"
                                href={businessDetail.facebook_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-facebook me-2"></i>Facebook
                              </a>
                            </li>
                          )}
                          {businessDetail.instagram_link && (
                            <li>
                              <a
                                className="dropdown-item"
                                href={businessDetail.instagram_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-instagram me-2"></i>
                                Instagram
                              </a>
                            </li>
                          )}
                          {businessDetail.twitter_link && (
                            <li>
                              <a
                                className="dropdown-item"
                                href={businessDetail.twitter_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-twitter me-2"></i>Twitter
                              </a>
                            </li>
                          )}
                          {businessDetail.youtube_link && (
                            <li>
                              <a
                                className="dropdown-item"
                                href={businessDetail.youtube_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-youtube me-2"></i>Youtube
                              </a>
                            </li>
                          )}
                          {businessDetail.linkedin_link && (
                            <li>
                              <a
                                className="dropdown-item"
                                href={businessDetail.linkedin_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="bi bi-linkedin me-2"></i>LinkedIn
                              </a>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row gymGallery">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body p-2">
                      <div className="row g-1">
                        {businessDetailGallery.map((galleryImg, index) => {
                          return (
                            <div className="col-6" key={index}>
                              <a
                                href="javascript(0);"
                                data-bs-toggle="modal"
                                data-bs-target="#GalleryModal"
                              >
                                <LazyLoadImage
                                  src={galleryImg || noimage}
                                  className="gymGallery_img"
                                  loading="lazy"
                                  alt={`${businessDetail.name || "Gym"} gallery photo ${index + 1}`}
                                  onError={(e) => { e.target.onerror = null; e.target.src = noimage; }}
                                />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <a
                      href="javascript(0);"
                      className="gallery-icon"
                      data-bs-toggle="modal"
                      data-bs-target="#GalleryModal"
                    >
                      <i className="bi bi-camera"></i>
                      <h6>View Gallery</h6>
                    </a>
                  </div>
                </div>

                {/*Gallery Modal Start */}
                <div
                  className="modal fade ps-0 modal-gallery"
                  id="GalleryModal"
                  aria-hidden="true"
                  aria-labelledby="GalleryModal"
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-dialog-centered modal-fullscreen">
                    <div className="modal-content">
                      <div className="modal-body bg-glossy">
                        <div className="container position-relative">
                          <button
                            type="button"
                            className="button-close"
                            data-bs-dismiss="modal"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                          <div className="row">
                            <div className="col-sm-12 position-relative px-0">
                              <Carousel
                                responsive={state}
                                autoPlay={true}
                                autoPlaySpeed={3000}
                                infinite={true}
                              >
                                {businessDetailGallery.map((galleryImg, index) => {
                                  return (
                                    <LazyLoadImage
                                      key={index}
                                      src={galleryImg || noimage}
                                      loading="lazy"
                                      className="gym-gallery"
                                      alt={`${businessDetail.name || "Gym"} gallery photo ${index + 1}`}
                                      onError={(e) => { e.target.onerror = null; e.target.src = noimage; }}
                                    />
                                  );
                                })}
                              </Carousel>
                              <div className=" text-white text-end mt-2">
                                <h6>1 of {businessDetailGallery.length}</h6>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/*Gallery Modal End */}
              </div>
              <div className="row detail-about-row">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card detail-about-card">
                    <div className="card-body">
                      <h2 className="detail-address">About Us</h2>
                      <div className="detail-about-content text-secondary">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(businessDetail.about || ""),
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row detail-membership-row">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card detail-collapsible-card">
                    <div className="card-body">
                      <h2 className="detail-address">Gym Membership</h2>
                      <button
                        className="btn btn-warning w-100 text-start mt-2"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseExample"
                        aria-expanded="false"
                        aria-controls="collapseExample"
                      >
                        See Membership
                        <i className="bi bi-arrow-down-circle svg-down fs-5"></i>
                      </button>

                      <div
                        className="collapsible-table collapse"
                        id="collapseExample"
                      >
                        <table className="table m-0">
                          <tbody>
                            {planDetail.map((plan) => {
                              return (
                                <tr key={plan.id}>
                                  <td className="fw-bold" key={plan.id}>
                                    {plan.plan_name}
                                  </td>
                                  <td>Rs {plan.plan_amount}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="row detail-review-row">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card detail-review-card ">
                    <div className="card-body">
                      <h3 className="detail-address">Customer Reviews</h3>
                      <div className="mt-4">
                        <div className="d-flex align-items-center">
                          <span className="h4 mb-0">4.4</span>
                          <span>
                            <i className="bi bi-star-fill text-danger fs-4 ms-2"></i>
                          </span>
                          <span>
                            <i className="bi bi-star-fill text-danger fs-4 ms-2"></i>
                          </span>
                          <span>
                            <i className="bi bi-star-fill text-danger fs-4 ms-2"></i>
                          </span>
                          <span>
                            <i className="bi bi-star-fill text-danger fs-4 ms-2"></i>
                          </span>
                          <span>
                            <i className="bi bi-star text-danger fs-4 ms-2"></i>
                          </span>
                        </div>
                        <span className="text-secondary">212 Reviews</span>

                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="row align-items-center">
                              <div className="col-lg-2 col-md-2 col-4">
                                <span className="text-secondary">
                                  Excellent
                                </span>
                              </div>
                              <div className="col-lg-8 col-md-8 col-6">
                                <div className="bar-container">
                                  <div className="bar-4"></div>
                                </div>
                              </div>
                              <div className="col-2">
                                <span className="text-secondary">40%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-12">
                            <div className="row align-items-center">
                              <div className="col-lg-2 col-md-2 col-4">
                                <span className="text-secondary">Good</span>
                              </div>
                              <div className="col-lg-8 col-md-8 col-6">
                                <div className="bar-container">
                                  <div className="bar-3"></div>
                                </div>
                              </div>
                              <div className="col-2">
                                <span className="text-secondary">60%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-12">
                            <div className="row align-items-center">
                              <div className="col-lg-2 col-md-2 col-4">
                                <span className="text-secondary">Average</span>
                              </div>
                              <div className="col-lg-8 col-md-8 col-6">
                                <div className="bar-container">
                                  <div className="bar-2"></div>
                                </div>
                              </div>
                              <div className="col-2">
                                <span className="text-secondary">20%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-12">
                            <div className="row align-items-center">
                              <div className="col-lg-2 col-md-2 col-4">
                                <span className="text-secondary">Poor</span>
                              </div>
                              <div className="col-lg-8 col-md-8 col-6">
                                <div className="bar-container">
                                  <div className="bar-1"></div>
                                </div>
                              </div>
                              <div className="col-2">
                                <span className="text-secondary">5%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="py-4 text-center">
                          <button className="btn btn-outline-danger rounded-1">
                            Write A Review
                          </button>
                        </div>
                        <div className="row mt-2">
                          <div className="col-12">
                            <div className="card">
                              <div className="card-header bg-white border-0">
                                <div className="row">
                                  <div className="col-6">
                                    <span>
                                      <i className="bi bi-star-fill text-danger"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star-fill text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star-fill text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star-fill text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star text-danger ms-2"></i>
                                    </span>
                                    <h6 className="mt-1">Abhishek Basak</h6>
                                  </div>
                                  <div className="col-6">
                                    <div className="text-end">
                                      <span className="text-secondary">
                                        09/09/2023
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="card-body pt-0">
                                <div className="row">
                                  <div className="col-12">
                                    <span className="text-secondary">
                                      This is very best gym in varanasi for your
                                      fitness training.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-12">
                            <div className="card">
                              <div className="card-header bg-white border-0">
                                <div className="row">
                                  <div className="col-6">
                                    <span>
                                      <i className="bi bi-star-fill text-danger"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star-fill text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star-fill text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star text-danger ms-2"></i>
                                    </span>
                                    <span>
                                      <i className="bi bi-star text-danger ms-2"></i>
                                    </span>
                                    <h6 className="mt-1">Vishal Yadav</h6>
                                  </div>
                                  <div className="col-6">
                                    <div className="text-end">
                                      <span className="text-secondary">
                                        09/09/2023
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="card-body pt-0">
                                <div className="row">
                                  <div className="col-12">
                                    <span className="text-secondary">
                                      A gym isn’t just a place for exercise;
                                      it’s the place you go to unwind, socialize
                                      and work out. The gym is a whole
                                      experience. Some of the most successful
                                      facilities have several gym features that
                                      contribute to the kind of member
                                      experience that drives retention and
                                      sales.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="row detail-help">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card detail-help-card">
                    <div className="card-body">
                      <h5 className="detail-address">Talk to gym?</h5>
                      <div className="row detail-help-sub-row">
                        {businessDetail.phone && (
                          <div className="col-12 col-sm-6 d-flex align-items-center mb-2 mb-sm-0">
                            <i className="bi bi-telephone-inbound text-danger fs-5"></i>
                            <Link to="#" className="ms-3 detail-title">
                              {businessDetail.phone}
                            </Link>
                          </div>
                        )}
                        {businessDetail.email && (
                          <div className="col-12 col-sm-6 d-flex align-items-center">
                            <i className="bi bi-envelope-at text-danger fs-5"></i>
                            <Link to="#" className="ms-3 detail-title">
                              {businessDetail.email}
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row detail-help">
                <div className="col-sm-12 col-md-12 col-xs-12">
                  <div className="card detail-help-card">
                    <div className="card-body">
                      <h2 className="detail-address mb-3">Locate Us</h2>
                      <div className="ratio ratio-16x9">
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d115408.09799641083!2d82.90870559356667!3d25.32089492193983!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x398e2db76febcf4d%3A0x68131710853ff0b5!2sVaranasi%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1706701261174!5m2!1sen!2sin"
                          style={{ border: "0", borderRadius: "5px" }}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Company Registered At"
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-4">
              {/* WhatsApp CTA — highest conversion action */}
              {businessDetail.phone && (
                <div className="mg-whatsapp-card mb-3">
                  <a
                    href={`https://wa.me/91${businessDetail.phone.replace(/\D/g, "")}?text=Hi%2C%20I%E2%80%99m%20interested%20in%20joining%20${encodeURIComponent(businessDetail.name || "your gym")}%20and%20would%20like%20to%20know%20more%20about%20membership%20plans.`}
                    className="mg-whatsapp-btn"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="bi bi-whatsapp me-2"></i>
                    WhatsApp the Gym
                  </a>
                  <a
                    href={`tel:${businessDetail.phone}`}
                    className="mg-call-btn mt-2"
                  >
                    <i className="bi bi-telephone-fill me-2"></i>
                    Call Now
                  </a>
                </div>
              )}

              <div className="card detail-inquiry-card">
                <div className="card-body">
                  <h5 className="detail-address text-dark detail-inquiry-head">
                    Send Enquiry
                  </h5>
                  <form
                    className="request-form"
                    action="/request.php"
                    name="inquiry-form"
                  >
                    <div className="mt-4">
                      <input
                        type="text"
                        className="form-control mt-3"
                        id="user-name"
                        placeholder="Enter your name"
                        required
                      />
                      <input
                        type="email"
                        className="form-control mt-3"
                        id="user-email"
                        placeholder="Enter your email address"
                        required
                      />
                      <input
                        type="number"
                        className="form-control mt-3"
                        id="user-number"
                        placeholder="Enter your mobile number"
                        required
                      />
                      <textarea
                        className="form-control mt-3"
                        rows="3"
                        name="text"
                        id="text-query"
                        placeholder="Please enter your queries"
                        required
                      />
                      <button
                        type="submit"
                        className="btn btn-danger btn-border-0 w-100 mt-3"
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
